/* eslint-disable no-console */
import { ChatUserAndToken } from "@virtualprogaming/js-public-sdk";
import React, { createContext, useContext, useEffect, useState } from "react";

import { CHAT_TOKEN_EXPIRE_TIME } from "~/constants";
import {
  useChatAuthUser,
  useChatRefresh,
  useChatValidateToken,
} from "~/queries";
import { useIsLoggedIn } from "~/utils";

export interface ContextProps {
  messageTheme: MessageTheme;
  setMessageTheme: (messageTheme: MessageTheme) => void;
  toggleMessageTheme: () => void;
  isChatOpen: boolean;
  setIsChatOpen: (isChatOpen: boolean) => unknown;
  selectedChat: number | null;
  setSelectedChat: (selectedChat: number | null) => void;
  isMenuOpened: boolean;
  setIsMenuOpened: (isMenuOpened: boolean) => void;
  messageToEdit: MessageToEditProps | null;
  setMessageToEdit: (messageToEdit: MessageToEditProps | null) => void;
  isChatAuthUserLoading: boolean;
  chatAuthUser: ChatUserAndToken | undefined;
  selectedWindow: SelectedWindows;
  setSelectedWindow: (window: SelectedWindows) => void;
  chatToRename: number | null;
  setChatToRename: (chatToRename: number | null) => void;
  setIsUserReloging: (isReloging: boolean) => void;
  shouldRefresh: boolean;
  setShouldRefresh: (shouldRefresh: boolean) => void;
  handleShouldRefresh: (shouldRefresh: boolean, cb: () => unknown) => void;
}

export const ChatContext = createContext({
  isChatOpen: false,
  selectedChat: null,
  isMenuOpened: true,
  messageToEdit: null,
  isChatAuthUserLoading: false,
  chatToRename: null,
  selectedWindow: "" as SelectedWindows,
  shouldRefresh: false,
} as ContextProps);

interface MessageToEditProps {
  id: number;
  text: string;
}

export enum SelectedWindows {
  None = "none",
  Conversation = "conversation",
  Notifications = "notifications",
  CreateChat = "create-chat",
  SystemChat = "system-chat",
}

export enum MessageTheme {
  Dark = "dark",
  Light = "light",
}

export const ChatContextProvider: React.FC = ({ children }) => {
  const [messageTheme, setMessageTheme] = useState<MessageTheme>(
    MessageTheme.Dark
  );
  const isLoggedIn = useIsLoggedIn();
  const {
    data: chatAuthUser,
    isLoading: isChatAuthUserLoading,
    refetch,
  } = useChatAuthUser(isLoggedIn);
  const [chatUser, setChatUser] = useState<ChatUserAndToken>(
    {} as ChatUserAndToken
  );

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedWindow, setSelectedWindow] = useState<SelectedWindows>(
    SelectedWindows.None
  );
  const [isMenuOpened, setIsMenuOpened] = useState(true);
  const setChatOpen = (chatOpen: boolean) => {
    setIsChatOpen(chatOpen);
  };
  const [chatToRename, setChatToRename] = useState<number | null>(null);

  const [selectedChat, setSelectedChat] = useState<number | null>(null);
  const [messageToEdit, setMessageToEdit] = useState<MessageToEditProps | null>(
    null
  );

  const [isUserReloging, setIsUserReloging] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const {
    mutateAsync: refreshToken,
    isLoading: isRefreshing,
  } = useChatRefresh();
  const { data: tokenStatus } = useChatValidateToken(chatUser.access_token);

  const toggleMessageTheme = () => {
    setMessageTheme((prevMessageTheme: MessageTheme) => {
      if (prevMessageTheme === MessageTheme.Dark) {
        localStorage.setItem("message-theme", MessageTheme.Light);
        return MessageTheme.Light;
      } else {
        localStorage.setItem("message-theme", MessageTheme.Dark);

        return MessageTheme.Dark;
      }
    });
  };

  useEffect(() => {
    const theme = localStorage.getItem("message-theme");
    if (theme) {
      setMessageTheme(theme as MessageTheme);
    }
  }, []);

  const handleShouldRefresh = (shouldRefresh: boolean, cb: () => void) => {
    setShouldRefresh(shouldRefresh);
    cb();
  };

  useEffect(() => {
    if (
      !chatAuthUser?.access_token &&
      !chatAuthUser?.user &&
      isLoggedIn &&
      !chatUser?.access_token &&
      !chatUser?.user
    ) {
      refetch();
    }
  }, [
    chatAuthUser,
    refetch,
    isLoggedIn,
    chatUser?.access_token,
    chatUser?.user,
  ]);

  useEffect(() => {
    if (
      !chatUser?.access_token &&
      chatAuthUser?.access_token &&
      chatAuthUser?.user
    ) {
      setIsUserReloging(true);
      refreshToken(CHAT_TOKEN_EXPIRE_TIME).then((res) => {
        setChatUser((prevChatUser: ChatUserAndToken) => ({
          user: prevChatUser.user,
          access_token: res.data,
        }));
        setIsUserReloging(false);
      });
      setChatUser(chatAuthUser);
    }
  }, [chatAuthUser, chatUser, refreshToken]);

  useEffect(() => {
    if (
      (shouldRefresh || (tokenStatus && tokenStatus !== 200)) &&
      chatUser?.user &&
      chatUser?.access_token &&
      !isChatAuthUserLoading &&
      !isUserReloging
    ) {
      setIsUserReloging(true);
      refreshToken(CHAT_TOKEN_EXPIRE_TIME).then((res) => {
        setIsUserReloging(false);
        setChatUser((prevChatUser: ChatUserAndToken) => ({
          user: prevChatUser.user,
          access_token: res.data,
        }));
        setShouldRefresh(false);
      });
    }
  }, [
    chatUser,
    isChatAuthUserLoading,
    isUserReloging,
    refreshToken,
    shouldRefresh,
    tokenStatus,
  ]);

  return (
    <ChatContext.Provider
      value={{
        messageTheme,
        setMessageTheme,
        toggleMessageTheme,
        isChatOpen,
        setIsChatOpen: setChatOpen,
        selectedChat,
        setSelectedChat,
        isMenuOpened,
        setIsMenuOpened,
        messageToEdit,
        setMessageToEdit,
        chatAuthUser: chatUser,
        isChatAuthUserLoading:
          isChatAuthUserLoading || isUserReloging || isRefreshing,
        selectedWindow,
        setSelectedWindow,
        chatToRename,
        setChatToRename,
        setIsUserReloging,
        shouldRefresh,
        setShouldRefresh,
        handleShouldRefresh,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const { ...values } = useContext(ChatContext);
  return values;
};

export const useChatSettings = () => {
  const { isChatOpen, setIsChatOpen } = useContext(ChatContext);

  return {
    isChatOpen,
    setIsChatOpen,
  };
};

export const useSelectedChat = () => {
  const { selectedChat, setSelectedChat } = useContext(ChatContext);

  return {
    selectedChat,
    setSelectedChat,
  };
};

export const useChatLeftMenuSettings = () => {
  const { isMenuOpened, setIsMenuOpened } = useContext(ChatContext);

  return {
    isMenuOpened,
    setIsMenuOpened,
  };
};

export const useChatMessageToEdit = () => {
  const { messageToEdit, setMessageToEdit } = useContext(ChatContext);

  return {
    messageToEdit,
    setMessageToEdit,
  };
};
export const useChatAuthAccessToken = () => {
  const { chatAuthUser } = useContext(ChatContext);

  return chatAuthUser?.access_token ?? "";
};
export const useChatAuthUserData = () => {
  const {
    chatAuthUser,
    isChatAuthUserLoading,
    setIsUserReloging,
    setShouldRefresh,
    handleShouldRefresh,
  } = useContext(ChatContext);

  return {
    chatAuthUser,
    isChatAuthUserLoading,
    setIsUserReloging,
    setShouldRefresh,
    handleShouldRefresh,
  };
};

export const useChatSelectedWindow = () => {
  const { selectedWindow, setSelectedWindow } = useContext(ChatContext);

  return {
    selectedWindow,
    setSelectedWindow,
  };
};

export const useChatToRename = () => {
  const { chatToRename, setChatToRename } = useContext(ChatContext);

  return {
    chatToRename,
    setChatToRename,
  };
};

export const useChatMessageTheme = () => {
  const { messageTheme, setMessageTheme, toggleMessageTheme } = useContext(
    ChatContext
  );

  return {
    messageTheme,
    setMessageTheme,
    toggleMessageTheme,
  };
};
